import usePayments, { Payment } from '@/hooks/queries/usePayments';
import Card from '../../ui/Card';
import CardTitle from '../../ui/CardTitle';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/Table';
import { Button } from '../../ui/Button';
import { BYTES_IN_GB } from '@fd/common/biz/formatBytesToGb';
import Flex from '../../ui/Flex';
import { useState } from 'react';
import TablePagination from './TablePagination';

export default function PaymentsTable() {
  const [currentPage, setCurrentPage] = useState(1);
  const { data, pages } = usePayments(currentPage);
  return (
    <Card>
      <CardTitle>Payment history</CardTitle>
      <Table>
        {!pages && <TableCaption>{"You don't have any payments"}</TableCaption>}
        <TableHeader>
          <TableRow>
            <TableHead>Payment</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Payment type</TableHead>
            <TableHead>Payment method</TableHead>
            <TableHead>Date</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data?.map((payment) => (
            <PaymentRow key={payment.id} payment={payment} />
          ))}
        </TableBody>
      </Table>
      <TablePagination
        currentPage={currentPage}
        pages={pages}
        onPageChange={setCurrentPage}
      />
    </Card>
  );
}

function PaymentRow({ payment }: { payment: Payment }) {
  const providerData = JSON.parse(payment.raw_provider_data as string);

  const paymentType =
    payment.payment_reason === 'one_time_payment'
      ? 'One time payment'
      : 'Subscription';

  const paymentMethod =
    payment.payment_gateway === 'stripe' ? 'Credit card' : 'Crypto';

  const paymentDate = new Date(payment.created_at)
    .toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
    .replace(' at', ',');

  const invoiceNumber = (() => {
    if (payment.payment_gateway === 'stripe') {
      return providerData.number;
    } else {
      return providerData.result.order_id;
    }
  })();

  const invoiceLink = (() => {
    if (payment.payment_gateway === 'stripe') {
      return providerData.hosted_invoice_url;
    } else {
      return providerData.result.url;
    }
  })();

  const products = (() => {
    const products = [];
    if (payment.subscription) {
      const subscription = payment.subscription;
      if (subscription.residential_bytes) {
        products.push(
          `Residential proxy (Shared) ${
            subscription.residential_bytes / BYTES_IN_GB
          } GB`
        );
      }
      if (subscription.datacenter_bytes) {
        products.push(
          `Datacenter proxy (Shared) ${
            subscription.datacenter_bytes / BYTES_IN_GB
          } GB`
        );
      }
      if (subscription.isp_bytes) {
        products.push(
          `ISP proxy (Shared) ${subscription.isp_bytes / BYTES_IN_GB} GB`
        );
      }
      if (subscription.mobile_bytes) {
        products.push(
          `Mobile proxy (Shared) ${subscription.mobile_bytes / BYTES_IN_GB} GB`
        );
      }
    } else if (payment.customer_order) {
      const order = payment.customer_order;
      if (order.residential_traffic_gb) {
        products.push(
          `Residential proxy (Shared) ${order.residential_traffic_gb} GB`
        );
      }
      if (order.datacenter_traffic_gb) {
        products.push(
          `Datacenter proxy (Shared) ${order.datacenter_traffic_gb} GB`
        );
      }
      if (order.mobile_traffic_gb) {
        products.push(`Mobile proxy (Shared) ${order.mobile_traffic_gb} GB`);
      }
      if (order.isp_traffic_gb) {
        products.push(`ISP proxy (Shared) ${order.isp_traffic_gb} GB`);
      }
    }
    return products;
  })();

  return (
    <TableRow key={payment.id} className="hover:bg-inherit">
      <TableCell>
        <Flex.Col className="gap-1">
          <span className="text-sm font-medium">{invoiceNumber}</span>
          {products.map((product) => (
            <span
              key={product}
              className="text-sm text-secondary whitespace-pre-wrap"
            >
              {product}
            </span>
          ))}
        </Flex.Col>
      </TableCell>
      <TableCell>${(payment.amount_cents / 100).toFixed(2)}</TableCell>
      <TableCell>{paymentType}</TableCell>
      <TableCell>{paymentMethod}</TableCell>
      <TableCell>{paymentDate}</TableCell>
      <TableCell className="">
        <Button
          variant="nooutline"
          className="bg-[#eee]"
          onClick={() => window.open(invoiceLink, '_blank')}
        >
          View receipt
        </Button>
      </TableCell>
    </TableRow>
  );
}
